import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoaderCircle, Pencil, Play, RefreshCw, Square, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GeminiVoiceProperties } from "@/components/admin/GeminiVoiceProperties";
import { SortableTableHeader } from "@/components/admin/SortableTableHeader";
import { supabase } from "@/lib/supabaseClient";
import { loadVoiceCatalog } from "@/lib/voiceModification";
import { nextTableSort, sortTableRows } from "@/lib/adminTableSort";
import { readVoiceSort, writeVoiceSort, type VoiceSort } from "@/lib/voiceSortPreference";
import { CATALOG_FACETS, catalogOptions, catalogValue, filterCatalog, hasVoicePreview, ingestedLabel, type CatalogFacet, type PreviewFilter, type VoiceStatusFilter } from "@/lib/geminiCatalogFilters";
import { sampleProgressLabel, sampleProgressDetail, type CatalogSampleJob } from "@/lib/catalogSampleProgress";
import { CatalogAudition, EMPTY_AUDITION } from "@/lib/catalogAudition";
import { catalogScanTimestamp, setCatalogVoiceActive } from "@/lib/catalogActivation";
import { catalogTraitOptions, catalogTraitValue, setCatalogVoiceAccent, setCatalogVoiceTrait } from "@/lib/catalogTraitEditing";
import { catalogAccentFilters, catalogAccentGroups, catalogAccentLabel, catalogAccentValue, countryAccentFilterKey, UNSPECIFIED_ACCENT } from "@/lib/catalogAccents";
import { readVoiceReviewView, writeVoiceReviewView } from "@/lib/adminReviewPreference";
import { auditionPageKey, clearCatalogVoiceReviewLocally, mergeCatalogVoiceReviews, readCatalogVoiceReview, readCatalogVoiceReviewClear, reviewMatchesSample, sampleHash, type CatalogVoiceReview, type CatalogVoiceReviewClear } from "@/lib/voiceCatalogReviews";

type VoiceRow = {
  id: string; display_name: string; provider: string; tags: string[] | null;
  sample_audio_url: string | null; test_phrase: string | null; is_active: boolean; is_narrator?: boolean;
  created_at?: string; updated_at?: string;
  provider_metadata?: Record<string, unknown> | null;
};
const INLINE_TRAITS = ['gender', 'pitch'] as const;

export default function GeminiVoicesTab({ userId }: { userId?: string }) {
  const [rows, setRows] = useState<VoiceRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [lastModified, setLastModified] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastScannedAt, setLastScannedAt] = useState<string | null>(null);
  const [scanStatusKnown, setScanStatusKnown] = useState(false);
  const propertyFlights = useRef(new Set<string>());
  const [propertySaving, setPropertySaving] = useState(new Set<string>());
  const [scanResult, setScanResult] = useState<{ new_count: number; scanned_count: number } | null>(null);
  const [sampleDetailsOpen, setSampleDetailsOpen] = useState(false);
  const [sampleJobs, setSampleJobs] = useState<CatalogSampleJob[]>([]);
  const [sampleVoices, setSampleVoices] = useState<VoiceRow[]>([]);
  const [sampleProgressUnavailable, setSampleProgressUnavailable] = useState(false);
  const sampleSequence = useRef(0);
  const scanFlight = useRef(false);
  const [savedView] = useState(() => readVoiceReviewView(userId));
  const [viewRestored, setViewRestored] = useState(false);
  const [query, setQuery] = useState(savedView.query);
  const [statusFilter, setStatusFilter] = useState<VoiceStatusFilter>(savedView.status);
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>(savedView.preview);
  const [filters, setFilters] = useState<Partial<Record<CatalogFacet,string>>>(savedView.filters);
  const [accentFilter, setAccentFilter] = useState(savedView.accentFilter);
  const [page, setPage] = useState(savedView.page);
  const [sort, setSort] = useState<VoiceSort | null>(() => { const saved = readVoiceSort(userId); return saved && ['gemini', 'language_code', 'region_code'].includes(saved.key) ? null : saved; });
  const tableSort = sort?.key === "tags" ? null : sort;
  const [editing, setEditing] = useState<VoiceRow | null>(null);
  const [auditionState, setAuditionState] = useState({ ...EMPTY_AUDITION });
  const audition = useRef<CatalogAudition | null>(null);
  const rowElements = useRef(new Map<string, HTMLTableRowElement>());
  const [reviews, setReviews] = useState(new Map<string, CatalogVoiceReview>());
  const reviewsRef = useRef(new Map<string, CatalogVoiceReview>());
  const reviewClears = useRef(new Map<string, CatalogVoiceReviewClear>());
  const reviewMutations = useRef(0);
  const currentPageKey = useRef('');
  const [reviewReadyKey, setReviewReadyKey] = useState<string | null>(null);
  const [clearingId, setClearingId] = useState<string | null>(null);
  const clearFlight = useRef(false);
  const [reviewsReload, setReviewsReload] = useState(0);
  const reviewReadSequence = useRef(0);
  const reviewWriteEpoch = useRef(0);
  const refreshSequence = useRef(0);
  const storeReviews = useCallback((update: (previous: Map<string, CatalogVoiceReview>) => Map<string, CatalogVoiceReview>) => {
    reviewsRef.current = update(reviewsRef.current);
    setReviews(reviewsRef.current);
  }, []);
  const readSampleProgress = useCallback(async () => {
    const sequence = ++sampleSequence.current;
    try {
      const result = await supabase.functions.invoke('gemini-catalog', { body: { action: 'sample-progress' } });
      if (sequence !== sampleSequence.current) return;
      if (result.error || !Array.isArray(result.data?.jobs) || !Array.isArray(result.data?.voices)) throw Error('Progress unavailable');
      setSampleJobs(result.data.jobs); setSampleVoices(result.data.voices); setSampleProgressUnavailable(false);
    } catch { if (sequence === sampleSequence.current) setSampleProgressUnavailable(true); }
  }, []);

  const stop = useCallback((resetPage = false, clearSelection = false) => audition.current?.stop(resetPage, clearSelection), []);
  useEffect(() => {
    const player = new CatalogAudition({
      savedSample: async voice => {
        // Read the saved recording. Auditioning never starts paid synthesis.
        const result = await supabase.functions.invoke("voice-sample", { body: { voice_id: voice.id } });
        if (result.error || typeof result.data?.signed_url !== 'string') throw Error('Saved sample unavailable');
        return result.data.signed_url;
      },
      createAudio: url => new Audio(url),
      requestId: () => crypto.randomUUID(),
      isVerified: voice => reviewMatchesSample(reviewsRef.current.get(voice.id), voice),
      verify: async (voice, eventId) => {
        reviewWriteEpoch.current++; reviewMutations.current++;
        try {
          const result = await supabase.rpc('record_catalog_voice_review', {
            p_voice: voice.id, p_sample: voice.sample_audio_url, p_sha256: sampleHash(voice), p_event: eventId,
          }).abortSignal(AbortSignal.timeout(15000));
          if (result.error) throw Error('Verification save failed');
          const review = readCatalogVoiceReview(result.data);
          if (!reviewMatchesSample(review, voice)) throw Error('Verification sample changed');
          return review;
        } finally { reviewWriteEpoch.current++; reviewMutations.current--; setReviewsReload(value => value + 1); }
      },
      onVerified: review => storeReviews(previous => mergeCatalogVoiceReviews(previous, [review], reviewClears.current)),
      onState: setAuditionState, onError: setError,
    });
    audition.current = player;
    return () => { player.dispose(); if (audition.current === player) audition.current = null; };
  }, []);

  const refresh = useCallback(async () => {
    const sequence = ++refreshSequence.current;
    setLoading(true); setError("");
    try {
      const catalog = await loadVoiceCatalog<VoiceRow>(supabase);
      if (sequence !== refreshSequence.current) return;
      setRows(catalog.rows); setTotalCount(catalog.totalCount); setLastModified(catalog.lastModified);
      setReviewsReload(value => value + 1);
      await Promise.all([readSampleProgress(), (async () => {
        try {
          const result = await supabase.rpc('get_gemini_catalog_scan_status');
          if (sequence !== refreshSequence.current) return;
          if (result.error) throw Error('Scan status unavailable');
          setLastScannedAt(catalogScanTimestamp(result.data)); setScanStatusKnown(true);
        } catch {
          if (sequence === refreshSequence.current) { setScanStatusKnown(false); setError('Unable to load the last Gemini scan date. Please try Refresh.'); }
        }
      })()]);
    } catch {
      if (sequence !== refreshSequence.current) return;
      setError("Unable to load the Gemini voice catalog. Please try Refresh.");
      setTotalCount(null); setLastModified(null);
    } finally {
      if (sequence === refreshSequence.current) setLoading(false);
    }
  }, [readSampleProgress]);

  useEffect(() => {
    void refresh();
    return () => { refreshSequence.current++; };
  }, [refresh]);
  useEffect(() => { writeVoiceSort(userId, sort); }, [userId, sort]);
  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) void readSampleProgress(); }, 5000);
    return () => { window.clearInterval(timer); sampleSequence.current++; };
  }, [readSampleProgress]);
  // Progress updates must not reset filters, sorting, or the current table page.
  const catalogRows = useMemo(() => {
    const ready = new Map(sampleVoices.map(row => [row.id, row]));
    return rows.map(row => { const newer = ready.get(row.id); return newer && (newer.updated_at ?? '') >= (row.updated_at ?? '') ? newer : row; });
  }, [rows, sampleVoices]);
  const samplesByVoice = useMemo(() => new Map(sampleJobs.map(job => [job.voice_id, job])), [sampleJobs]);
  const traitOptions = useMemo(() => Object.fromEntries(INLINE_TRAITS
    .map(facet => [facet, catalogTraitOptions(catalogRows, facet)])) as Record<typeof INLINE_TRAITS[number], ReturnType<typeof catalogTraitOptions>>, [catalogRows]);
  const accentGroups = useMemo(() => catalogAccentGroups(catalogRows, true), [catalogRows]);
  const accentFilterGroups = useMemo(() => catalogAccentGroups(catalogRows), [catalogRows]);

  const filteredRows = useMemo(() => {
    const filtered = filterCatalog(catalogRows, query, statusFilter, { ...filters, ...catalogAccentFilters(accentFilter) }, previewFilter);
    return tableSort ? sortTableRows(filtered, row => tableSort.key === "name" ? row.display_name : tableSort.key === "created_at" ? Date.parse(row.created_at ?? "") || null : tableSort.key === 'accent' ? catalogAccentLabel(row) : catalogValue(row,tableSort.key as CatalogFacet), tableSort.direction) : filtered;
  }, [catalogRows, query, statusFilter, sort, filters, previewFilter, accentFilter]);
  // Skip the initial reset, including StrictMode's repeated mount effects, so
  // a restored page survives. Later filter/sort changes still start at page one.
  const filterKey = JSON.stringify([query, statusFilter, sort, filters, previewFilter, accentFilter]);
  const previousFilterKey = useRef(filterKey), initialFilterKey = useRef(filterKey);
  useEffect(() => {
    if (previousFilterKey.current !== filterKey) { previousFilterKey.current = filterKey; setPage(0); }
  }, [filterKey]);
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / 50));
  const visiblePage = Math.min(page, pageCount - 1);
  const visibleRows = filteredRows.slice(visiblePage * 50, (visiblePage + 1) * 50);
  const pageKey = auditionPageKey(visibleRows);
  currentPageKey.current = pageKey;
  const hasUnverified = visibleRows.some(voice => !reviewMatchesSample(reviews.get(voice.id), voice));
  useEffect(() => { audition.current?.setPageContext(visibleRows); }, [pageKey]);
  useEffect(() => {
    if (viewRestored || loading || totalCount === null) return;
    const restoredIndex = filterKey === initialFilterKey.current && savedView.currentVoiceId
      ? filteredRows.findIndex(voice => voice.id === savedView.currentVoiceId) : -1;
    if (restoredIndex >= 0) {
      const restoredPage = Math.floor(restoredIndex / 50);
      if (visiblePage !== restoredPage) { setPage(restoredPage); return; }
      // Selection only: a reload must never play or verify audio on its own.
      audition.current?.select(savedView.currentVoiceId!);
    }
    setViewRestored(true);
  }, [viewRestored, loading, totalCount, pageKey, filterKey, visiblePage]);
  useEffect(() => {
    if (viewRestored && !loading && totalCount !== null) writeVoiceReviewView(userId, {
      query, status: statusFilter, preview: previewFilter, filters, accentFilter, page: visiblePage, currentVoiceId: auditionState.currentId,
    });
  }, [userId, viewRestored, loading, totalCount, query, statusFilter, previewFilter, filters, accentFilter, visiblePage, auditionState.currentId]);
  const readReviewsForPage = useCallback(async (voices: VoiceRow[]) => {
    if (reviewMutations.current) return false;
    const sequence = ++reviewReadSequence.current, epoch = reviewWriteEpoch.current;
    const ids = voices.map(voice => voice.id), key = auditionPageKey(voices);
    setReviewReadyKey(null);
    if (!ids.length) { setReviewReadyKey(key); return true; }
    try {
        const result = await supabase.rpc('get_catalog_voice_reviews', { p_voices: ids }).abortSignal(AbortSignal.timeout(15000));
        if (sequence !== reviewReadSequence.current || epoch !== reviewWriteEpoch.current || key !== currentPageKey.current) return false;
        if (result.error || !Array.isArray(result.data)) throw Error('Review status unavailable');
        const received = result.data.map(readCatalogVoiceReview);
        if (received.some(review => !ids.includes(review.voice_id))) throw Error('Invalid review page');
        storeReviews(previous => { const next = new Map(previous); for (const id of ids) next.delete(id); return mergeCatalogVoiceReviews(next, received, reviewClears.current); });
        setReviewReadyKey(key); return true;
    } catch {
      if (sequence === reviewReadSequence.current && epoch === reviewWriteEpoch.current) setError('Unable to load verification timestamps. Please try Refresh.');
      return false;
    }
  }, [storeReviews]);
  useEffect(() => {
    void readReviewsForPage(visibleRows);
    return () => { reviewReadSequence.current++; };
  }, [pageKey, reviewsReload]);
  useEffect(() => {
    if (!auditionState.currentId || editing) return;
    // Keep the stop target stationary. Only reveal rows that are out of view;
    // an animated whole-page scroll can move a pointer target mid-click.
    rowElements.current.get(auditionState.currentId)?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }, [auditionState.currentId, editing]);

  async function playUnverifiedPage() {
    if (clearFlight.current || reviewMutations.current) return;
    const ready = await readReviewsForPage(visibleRows);
    if (ready && !clearFlight.current && !reviewMutations.current && pageKey === currentPageKey.current) void audition.current?.playPage(visibleRows);
  }

  async function clearVerification(voice: VoiceRow) {
    const review = reviewsRef.current.get(voice.id);
    if (clearFlight.current || reviewMutations.current || !reviewMatchesSample(review, voice)) return;
    clearFlight.current = true; stop(true); setClearingId(voice.id); setError('');
    reviewWriteEpoch.current++; reviewMutations.current++; setReviewReadyKey(null);
    try {
      const result = await supabase.rpc('clear_catalog_voice_review', {
        p_voice: voice.id, p_sample: review!.sample_audio_url, p_sha256: review!.sample_sha256,
        p_verified_at: review!.verified_at, p_event: crypto.randomUUID(),
      }).abortSignal(AbortSignal.timeout(15000));
      if (result.error) throw Error('Verification clear failed');
      const cleared = readCatalogVoiceReviewClear(result.data);
      if (!reviewMatchesSample({ ...cleared, verified_at: cleared.cleared_through }, voice) || Date.parse(cleared.cleared_through) !== Date.parse(review!.verified_at)) throw Error('Clear response changed');
      reviewClears.current.set(voice.id, cleared);
      storeReviews(previous => clearCatalogVoiceReviewLocally(previous, cleared));
    } catch { setError(`Unable to clear verification for ${voice.display_name}. Refresh to check its current status.`); }
    finally {
      reviewWriteEpoch.current++; reviewMutations.current--; clearFlight.current = false;
      setClearingId(null); setReviewsReload(value => value + 1);
    }
  }

  async function scan() {
    if (scanFlight.current) return;
    scanFlight.current = true; setScanning(true); setError(""); stop(true, true);
    try {
      const storageKey = "tableread.admin.catalog-scan";
      const requestId = sessionStorage.getItem(storageKey) || crypto.randomUUID();
      sessionStorage.setItem(storageKey, requestId);
      const result = await supabase.functions.invoke("gemini-catalog", { body: { action: "scan", request_id: requestId } });
      let detail = result.data;
      if (result.error?.context instanceof Response) { try { detail = await result.error.context.json(); } catch { /* safe fallback */ } }
      if (result.error || detail?.error) throw new Error(detail?.error || "Scan interrupted. Try again to recover the result without duplicate voices.");
      if (!Number.isSafeInteger(detail?.new_count) || detail.new_count < 0 || !Number.isSafeInteger(detail?.scanned_count) || detail.scanned_count < detail.new_count) throw new Error("Invalid scan result. Try again to recover it.");
      sessionStorage.removeItem(storageKey); setScanResult(detail); await refresh();
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to scan for new voices."); }
    finally { scanFlight.current = false; setScanning(false); }
  }

  async function saveInlineProperties(voice: VoiceRow, save: () => Promise<void>) {
    if (propertyFlights.current.has(voice.id)) return;
    propertyFlights.current.add(voice.id); setPropertySaving(new Set(propertyFlights.current)); audition.current?.select(voice.id); setError('');
    try {
      await save();
      // Re-read authoritative rows, including refreshed sample snapshots. Do not
      // optimistically claim activation or overwrite concurrent label changes.
      await refresh();
    } catch (failure) { setError(failure instanceof Error ? failure.message : 'Unable to save voice properties.'); }
    finally { propertyFlights.current.delete(voice.id); setPropertySaving(new Set(propertyFlights.current)); }
  }

  async function toggleActive(voice: VoiceRow, active: boolean) {
    if (active === voice.is_active) return;
    await saveInlineProperties(voice, () => setCatalogVoiceActive(supabase, voice, active));
  }

  async function changeTrait(voice: VoiceRow, facet: CatalogFacet, value: string) {
    if (value === catalogTraitValue(voice, facet)) return;
    await saveInlineProperties(voice, () => setCatalogVoiceTrait(supabase, voice, facet, value));
  }

  async function changeAccent(voice: VoiceRow, value: string) {
    if (value === catalogAccentValue(voice)) return;
    await saveInlineProperties(voice, () => setCatalogVoiceAccent(supabase, voice, value));
  }

  return <div className="space-y-4">
    <p className="text-sm text-muted-foreground">The saved Gemini Flash-Lite catalog is the source for the frontend voice library. Inactive voices remain here until their saved samples are verified and they are enabled. Edit a voice to correct its display traits without changing its sound.</p>
    <Card>
      <CardHeader className="sticky top-0 z-20 border-b bg-card"><CardTitle className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input aria-label="Search voices" placeholder="Search voices…" value={query} onChange={event => { stop(true, true); setQuery(event.target.value); }} className="w-64" />
          <div className="flex items-center gap-2">
            <Label htmlFor="voice-filter-status" className="text-sm font-normal">Status</Label>
            <select id="voice-filter-status" aria-label="Voice status" value={statusFilter} onChange={event => { stop(true, true); setStatusFilter(event.target.value as VoiceStatusFilter); }} className="h-10 rounded-md border border-input bg-background px-2 text-sm font-normal">
              <option value="all">All</option><option value="active">Active</option><option value="inactive">Inactive</option>
            </select>
          </div>
          <Button type="button" size="icon" variant="secondary" aria-label="Refresh voices" disabled={loading} onClick={() => { stop(); void refresh(); }}>
            <RefreshCw aria-hidden="true" className={`h-4 w-4 ${loading ? "animate-spin motion-reduce:animate-none" : ""}`} />
          </Button>
          <Button type="button" variant={auditionState.pagePlaying ? 'default' : 'outline'} disabled={!auditionState.pagePlaying && (loading || scanning || !!clearingId || reviewReadyKey !== pageKey || !hasUnverified)}
            title="Play only unverified saved samples on this page in table order; stop to edit the highlighted voice."
            onClick={() => { if (auditionState.pagePlaying) stop(); else void playUnverifiedPage(); }}>
            {auditionState.pagePlaying ? <Square aria-hidden="true" className="mr-2 h-4 w-4" /> : <Play aria-hidden="true" className="mr-2 h-4 w-4" />}
            {auditionState.pagePlaying ? 'Stop page' : auditionState.canResume ? 'Resume page' : 'Play page'}
          </Button>
          <Button type="button" variant="outline" disabled={scanning || loading} onClick={() => void scan()}>
            {scanning && <LoaderCircle aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
            {scanning ? "Scanning voices…" : "Scan for new voices"}
          </Button>
        </div>
        <span className="ml-auto shrink-0 text-sm font-normal text-muted-foreground" role="status" aria-live="polite" title="All Gemini voices, including inactive voices.">Total voices: {totalCount === null ? loading ? "Loading…" : "—" : totalCount.toLocaleString()}</span>
      </CardTitle></CardHeader>
      <CardContent>
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="voice-filter-accent">Accent</Label>
            <select id="voice-filter-accent" aria-label="Accent filter" value={accentFilter}
              onChange={event => { stop(true, true); setAccentFilter(event.target.value); }} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">All accents</option>
              {accentFilterGroups.map(group => <optgroup key={group.country} label={group.label}>
                <option value={countryAccentFilterKey(group.country)}>{group.label} &gt; All accents</option>
                {group.accents.map(accent => <option key={accent.value} value={accent.value}>{accent.label}</option>)}
              </optgroup>)}
            </select>
          </div>
          {INLINE_TRAITS.map(facet => <div key={facet} className="min-w-0 space-y-1">
            <Label htmlFor={`voice-filter-${facet}`}>{CATALOG_FACETS[facet]}</Label>
            <select id={`voice-filter-${facet}`} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters[facet] ?? ""}
              onChange={event => { stop(true, true); setFilters(old=>({...old,[facet]:event.target.value})); }}>
              <option value="">All</option>{catalogOptions(rows,facet).map(value=><option key={value} value={value}>{value}</option>)}
            </select>
          </div>)}
          <div className="min-w-0 space-y-1">
            <Label htmlFor="voice-filter-preview">Voice preview</Label>
            <select id="voice-filter-preview" value={previewFilter} onChange={event => { stop(true, true); setPreviewFilter(event.target.value as PreviewFilter); }} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">All</option><option value="available">Available</option><option value="missing">Missing</option>
            </select>
          </div>
        </div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span role="status">{filteredRows.length.toLocaleString()} matching · {catalogRows.filter(row=>row.is_active).length.toLocaleString()} active · Last Gemini scan: {scanStatusKnown ? lastScannedAt ? <time dateTime={lastScannedAt}>{ingestedLabel(lastScannedAt)}</time> : 'Never' : loading ? 'Checking…' : 'Unavailable'}</span>
          <Button type="button" size="sm" variant="outline" onClick={()=>{stop(true,true);setQuery("");setFilters({});setAccentFilter('');setStatusFilter("all");setPreviewFilter("all");}}>Clear filters</Button>
        </div>
        {error && <p role="alert" className="mb-3 text-sm text-red-400">{error}</p>}
        <div className="overflow-x-auto" role="region" aria-label="Voice actors table" tabIndex={0}><table className="w-full min-w-[56rem] text-sm">
          <thead><tr className="text-left text-muted-foreground">
            <SortableTableHeader column="name" label="Name" sort={tableSort} onSort={key => { stop(true,true); setSort(current => nextTableSort(current, key)); }} className="sticky left-0 z-10 min-w-28 bg-card py-2 pr-3" />
            <th scope="col" className="py-2 pr-3">Actions</th>
            <SortableTableHeader column="accent" label="Accent" sort={tableSort} onSort={key => { stop(true,true); setSort(current => nextTableSort(current,key)); }} className="py-2 pr-3" />
            {INLINE_TRAITS.map(facet => <SortableTableHeader key={facet} column={facet} label={CATALOG_FACETS[facet]} sort={tableSort} onSort={key => { stop(true,true); setSort(current => nextTableSort(current,key)); }} className="py-2 pr-3" />)}
            <th scope="col" className="py-2 pr-3">Active</th>
            <SortableTableHeader column="created_at" label="Ingested" sort={tableSort} onSort={key => { stop(true,true); setSort(current => nextTableSort(current, key)); }} className="py-2 pr-3" />
            <th scope="col" className="py-2 pr-3">Sample</th>
            <th scope="col" className="py-2 pr-3">Verified</th>
          </tr></thead>
          <tbody>{visibleRows.map(voice => <tr key={voice.id} ref={node => { if (node) rowElements.current.set(voice.id,node); else rowElements.current.delete(voice.id); }}
            data-voice-id={voice.id} aria-current={auditionState.currentId === voice.id ? 'true' : undefined}
            className={`scroll-mt-40 border-t ${auditionState.currentId === voice.id ? 'bg-muted ring-2 ring-inset ring-primary' : ''}`}>
            <td className={`sticky left-0 z-10 py-3 pr-3 font-medium ${auditionState.currentId === voice.id ? 'bg-muted' : 'bg-card'}`}>{voice.display_name}</td>
            <td className="py-3 pr-3"><Button type="button" size="icon" variant="secondary" disabled={propertySaving.has(voice.id)} aria-label={`Edit ${voice.display_name}`} onClick={() => { audition.current?.select(voice.id); setEditing(voice); }}><Pencil aria-hidden="true" className="h-4 w-4" /></Button></td>
            <td className="py-3 pr-3">
              <select aria-label={`Accent: ${voice.display_name}`} value={catalogAccentValue(voice)} title={catalogAccentLabel(voice)}
                disabled={propertySaving.has(voice.id)} aria-busy={propertySaving.has(voice.id)} onFocus={() => audition.current?.select(voice.id)}
                onChange={event => void changeAccent(voice, event.target.value)} className="h-8 w-full min-w-44 max-w-64 rounded-md border border-input bg-background px-1.5 text-sm">
                <option value={UNSPECIFIED_ACCENT}>Not specified</option>
                {accentGroups.map(group => <optgroup key={group.country} label={group.label}>
                  {group.accents.filter(accent => accent.value !== UNSPECIFIED_ACCENT).map(accent => <option key={accent.value} value={accent.value}>{accent.label}</option>)}
                </optgroup>)}
              </select>
            </td>
            {INLINE_TRAITS.map(facet => <td key={facet} className="py-3 pr-3">
              <select aria-label={`${CATALOG_FACETS[facet]}: ${voice.display_name}`} value={catalogTraitValue(voice, facet)}
                title={catalogValue(voice, facet)} disabled={propertySaving.has(voice.id)} aria-busy={propertySaving.has(voice.id)}
                onFocus={() => audition.current?.select(voice.id)} onChange={event => void changeTrait(voice, facet, event.target.value)}
                className="h-8 w-full min-w-20 rounded-md border border-input bg-background px-1.5 text-sm">
                {traitOptions[facet].map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </td>)}
            <td className="py-3 pr-3"><input type="checkbox" className="h-4 w-4 accent-primary" checked={voice.is_active}
              disabled={propertySaving.has(voice.id)} aria-label={`Active: ${voice.display_name}`} aria-busy={propertySaving.has(voice.id)}
              onChange={event => void toggleActive(voice, event.target.checked)} /></td>
            <td className="py-3 pr-3 min-w-44"><time dateTime={voice.created_at} title="First added to the TableRead catalog; Central time">{ingestedLabel(voice.created_at)}</time></td>
            <td className="py-3 pr-3">{hasVoicePreview(voice) ? <div className="flex items-center gap-2"><Button type="button" size="icon" variant="secondary" disabled={!!clearingId} onClick={() => void audition.current?.toggleVoice(voice)}
              aria-label={`${auditionState.playingId === voice.id || auditionState.preparingId === voice.id || auditionState.verifyingId === voice.id ? "Stop" : "Play"} ${voice.display_name} preview`} aria-busy={auditionState.preparingId === voice.id || auditionState.verifyingId === voice.id}>
              {auditionState.preparingId === voice.id || auditionState.verifyingId === voice.id ? <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin" /> : auditionState.playingId === voice.id ? <Square aria-hidden="true" className="h-4 w-4" /> : <Play aria-hidden="true" className="h-4 w-4" />}
            </Button>{samplesByVoice.get(voice.id)?.state === 'ready' && <button type="button" className="text-xs text-muted-foreground underline decoration-dotted underline-offset-4" aria-label={`Sample details for ${voice.display_name}`} onClick={() => setSampleDetailsOpen(true)}>{samplesByVoice.get(voice.id)?.duration?.toFixed(1)}s</button>}</div> : <button type="button" className="text-left text-xs text-muted-foreground underline decoration-dotted underline-offset-4" onClick={() => setSampleDetailsOpen(true)}
              aria-label={`Sample details for ${voice.display_name}`}>
              {sampleProgressLabel(samplesByVoice.get(voice.id), sampleProgressUnavailable)}
            </button>}</td>
            <td className="min-w-44 py-3 pr-3">{reviewMatchesSample(reviews.get(voice.id),voice)
              ? <div className="flex items-center gap-2"><time dateTime={reviews.get(voice.id)!.verified_at} title="Sample played in full; Central time">{ingestedLabel(reviews.get(voice.id)!.verified_at)}</time>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={!!clearingId || !!auditionState.verifyingId || reviewReadyKey !== pageKey}
                  aria-label={`Clear verification for ${voice.display_name}`} title="Clear verification" onClick={() => void clearVerification(voice)}>
                  {clearingId === voice.id ? <LoaderCircle aria-hidden="true" className="h-3.5 w-3.5 animate-spin" /> : <X aria-hidden="true" className="h-3.5 w-3.5" />}
                </Button></div>
              : <span aria-label="Not verified">—</span>}</td>
          </tr>)}
          {!loading && filteredRows.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No voices found.</td></tr>}</tbody>
        </table></div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" disabled={visiblePage === 0} onClick={()=>{stop(true,true);setPage(visiblePage-1);}}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {visiblePage+1} of {pageCount}</span>
          <Button variant="outline" size="sm" disabled={visiblePage+1 >= pageCount} onClick={()=>{stop(true,true);setPage(visiblePage+1);}}>Next</Button>
        </div>
      </CardContent>
    </Card>
    <footer className="text-xs text-muted-foreground" aria-live="polite">Last voice modification: {lastModified ? <time dateTime={lastModified}>{new Date(lastModified).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" })}</time> : loading ? "Loading…" : "Not available"}</footer>
    {editing && <GeminiVoiceProperties key={editing.id} voice={editing} onClose={() => setEditing(null)} onSaved={() => void refresh()} />}
    <Dialog open={scanResult !== null || sampleDetailsOpen} onOpenChange={open => { if (!open) { setScanResult(null); setSampleDetailsOpen(false); } }}>
      <DialogContent><DialogHeader><DialogTitle>{scanResult ? 'Voice scan complete' : 'Catalog sample preparation'}</DialogTitle>
        <DialogDescription>{scanResult && <>{scanResult.new_count.toLocaleString()} new {scanResult.new_count === 1 ? "voice" : "voices"} found in {scanResult.scanned_count.toLocaleString()} Flash-Lite voices checked. </>}Original 7–10 second samples are prepared automatically in the background. New voices stay inactive until reviewed and enabled; existing activation and actor settings are unchanged.</DialogDescription>
      </DialogHeader>
        {sampleProgressUnavailable ? <p role="status" className="text-sm">Sample progress is temporarily unavailable. Refresh to check again; closing this window does not stop server preparation.</p> : <div className="max-h-80 space-y-3 overflow-y-auto text-sm" aria-label="Automatic sample results">
          <p role="status">{sampleJobs.filter(job => job.state === 'ready').length} of {sampleJobs.length} samples ready</p>
          {sampleJobs.map(job => <div key={job.voice_id} className="border-t pt-2"><p className="font-medium">{job.name} · {sampleProgressLabel(job)}</p><p className="mt-1 text-muted-foreground">{sampleProgressDetail(job)}</p></div>)}
        </div>}
        <Button onClick={() => { setScanResult(null); setSampleDetailsOpen(false); }}>Done</Button>
      </DialogContent>
    </Dialog>
  </div>;
}
